import {Inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {DOCUMENT} from '@angular/common';

export type ThemePreference = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ColorSchemeService implements OnDestroy {
  private readonly storageKey = 'theme';

  private mediaQuery: MediaQueryList | null = null;
  private mediaListener: ((event: MediaQueryListEvent) => void) | null = null;

  private readonly _preference = new BehaviorSubject<ThemePreference>(this.readPreference());
  private readonly _DarkTheme = new BehaviorSubject<boolean>(
    this.resolveIsDark(this._preference.value),
  );

  readonly preference$ = this._preference.asObservable();
  readonly isDarkTheme = this._DarkTheme.asObservable();

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.applyThemeAttributes(this._DarkTheme.value);
    this.syncSystemListener(this._preference.value);
  }

  ngOnDestroy(): void {
    this.clearSystemListener();
  }

  get darkTheme$(): Observable<boolean> {
    return this._DarkTheme.asObservable();
  }

  get currentTheme(): boolean {
    return this._DarkTheme.value;
  }

  get currentPreference(): ThemePreference {
    return this._preference.value;
  }

  setThemePreference(preference: ThemePreference): void {
    localStorage.setItem(this.storageKey, preference);
    this._preference.next(preference);
    this.syncSystemListener(preference);
    this.applyResolvedTheme(this.resolveIsDark(preference));
  }

  setDarkTheme(themeStatus: boolean): void {
    this.setThemePreference(themeStatus ? 'dark' : 'light');
  }

  private readPreference(): ThemePreference {
    const raw = localStorage.getItem(this.storageKey);
    if (raw === 'dark' || raw === 'light' || raw === 'system') {
      return raw;
    }
    return 'system';
  }

  private resolveIsDark(preference: ThemePreference): boolean {
    if (preference === 'system') {
      return this.getSystemPrefersDark();
    }
    return preference === 'dark';
  }

  private getSystemPrefersDark(): boolean {
    return this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches ?? false;
  }

  private applyResolvedTheme(isDark: boolean): void {
    this.applyThemeAttributes(isDark);
    this._DarkTheme.next(isDark);
  }

  private syncSystemListener(preference: ThemePreference): void {
    this.clearSystemListener();
    if (preference !== 'system') {
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    this.mediaQuery = win.matchMedia('(prefers-color-scheme: dark)');
    this.mediaListener = (event: MediaQueryListEvent) => {
      this.applyResolvedTheme(event.matches);
    };
    this.mediaQuery.addEventListener('change', this.mediaListener);
  }

  private clearSystemListener(): void {
    if (this.mediaQuery && this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    }
    this.mediaQuery = null;
    this.mediaListener = null;
  }

  private applyThemeAttributes(isDark: boolean): void {
    const scheme = isDark ? 'dark' : 'light';
    this.document.body.setAttribute('color-scheme', scheme);
    this.document.documentElement.setAttribute('data-theme', scheme);
  }
}
